import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

export type ThemeMode = 'light' | 'dark' | 'system'
export type AccessibilityTheme = 'none' | 'a11y' | 'deuteranopia' | 'protanopia' | 'tritanopia' | 'monochromacy'

// Mirrors the wire contract of GET/PUT /api/preferences.
export interface UserPreferences {
  theme: ThemeMode
  accessibilityTheme: AccessibilityTheme
  calmMode: boolean
  // Preferred column order (package names) on the Terraform modules page;
  // empty = the API's default order.
  moduleOrder: string[]
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'system',
  accessibilityTheme: 'none',
  calmMode: false,
  moduleOrder: []
}

const THEME_MODES: ThemeMode[] = ['light', 'dark', 'system']
const ACCESSIBILITY_THEMES: AccessibilityTheme[] = ['none', 'a11y', 'deuteranopia', 'protanopia', 'tritanopia', 'monochromacy']

const STORAGE_KEY = 'tma-user-preferences'

// Server/localStorage values are untrusted — clamp each field to the known
// values so a bad payload can never break theme resolution.
export const sanitizePreferences = (raw: unknown): UserPreferences => {
  const value = (raw ?? {}) as Partial<UserPreferences>
  return {
    theme: THEME_MODES.includes(value.theme as ThemeMode) ? (value.theme as ThemeMode) : DEFAULT_PREFERENCES.theme,
    accessibilityTheme: ACCESSIBILITY_THEMES.includes(value.accessibilityTheme as AccessibilityTheme)
      ? (value.accessibilityTheme as AccessibilityTheme)
      : DEFAULT_PREFERENCES.accessibilityTheme,
    calmMode: typeof value.calmMode === 'boolean' ? value.calmMode : DEFAULT_PREFERENCES.calmMode,
    moduleOrder: Array.isArray(value.moduleOrder)
      ? value.moduleOrder.filter((v): v is string => typeof v === 'string').slice(0, 200)
      : DEFAULT_PREFERENCES.moduleOrder
  }
}

// Last-known preferences, cached so the theme is right at first paint (before
// auth exists). The server copy wins once PreferencesSync fetches it. Logout
// clears localStorage — the next login simply re-syncs from the server.
const readCache = (): UserPreferences => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? sanitizePreferences(JSON.parse(stored)) : DEFAULT_PREFERENCES
  } catch {
    return DEFAULT_PREFERENCES
  }
}

interface PreferencesContextValue {
  preferences: UserPreferences
  setPreferences: (next: UserPreferences) => void
}

const PreferencesContext = createContext<PreferencesContextValue>({
  preferences: DEFAULT_PREFERENCES,
  setPreferences: () => {}
})

// Pure preference state; sits above ThemeController so preferences can drive
// the theme. Persistence to the API lives in PreferencesSync/useUserPreferences.
export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preferences, setPreferencesState] = useState<UserPreferences>(readCache)

  const setPreferences = useCallback((next: UserPreferences) => setPreferencesState(sanitizePreferences(next)), [])

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences))
    } catch {
      // Storage unavailable (private mode/quota) — preferences just won't be cached.
    }
  }, [preferences])

  return <PreferencesContext.Provider value={{ preferences, setPreferences }}>{children}</PreferencesContext.Provider>
}

export const usePreferences = () => useContext(PreferencesContext)

// Convenience flag for calm ("readability") rendering on the tenant detail page.
export const useCalmMode = () => usePreferences().preferences.calmMode
